import { CommonModule } from '@angular/common';
import { Component, DestroyRef, Inject, Input, OnChanges, OnDestroy, signal, SimpleChanges, DOCUMENT } from '@angular/core';
import { MatTableModule } from '@angular/material/table';
import { AppointmentDocument } from '@app/shared/interfaces/appointment-document.interface';
import { DocumentsService } from '@app/features/workCompDashboard/services/documents.service';
import { of, take } from 'rxjs';
import { rxResource, takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatIcon } from '@angular/material/icon';
import { ToastService } from '@app/core/services/toast.service';

interface DocumentsTableRow {
  id: string;
  document: string;
  url: string | null;
  date: string;
  provider: string;
}

@Component({
  selector: 'app-documents-table',
  imports: [
    CommonModule,
    MatTableModule,
    MatProgressSpinner,
    MatIcon
  ],
  templateUrl: './documents-table.component.html',
  styleUrl: './documents-table.component.scss'
})
export class DocumentsTableComponent implements OnChanges, OnDestroy {
  private static readonly PRINT_FRAME_CLEANUP_DELAY_MS = 5 * 60 * 1000;

  @Input() defaultProvider = 'No provider';
  @Input() patientId?: string;
  @Input() patientFirstName = '';
  @Input() patientLastName = '';
  @Input() encounterId?: string;
  @Input() appointmentDate?: string;
  readonly displayedColumns: string[] = ['document', 'date', 'provider'];
  printingDocumentId: string | null = null;

  private printFrame: HTMLIFrameElement | null = null;
  private printUrl: string | null = null;
  private originalDocumentTitle: string | null = null;
  private printFrameCleanupTimeoutId: ReturnType<typeof setTimeout> | null = null;

  appointmentDocumentSignal = signal<{ patientId: string | null; encounterId: string | null; } | null>(null);
  appointmentDocumentData = rxResource<AppointmentDocument[] | null, { patientId: string | null; encounterId: string | null; } | null>({
    params: () => this.appointmentDocumentSignal(),
    stream: (params) => {
      if (!params.params?.patientId || !params.params.encounterId) {
        return of(null);
      }
      return this.documentService.getDocumentsByEncounter$(params.params.patientId, params.params.encounterId);
    }
  });

  constructor(
    @Inject(DOCUMENT) private readonly document: Document,
    private readonly destroyRef: DestroyRef,
    private readonly documentService: DocumentsService,
    private readonly toastService: ToastService
  ) {
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['patientId'] || changes['encounterId']) {
      this.appointmentDocumentSignal.set({patientId: this.patientId ?? null, encounterId: this.encounterId ?? null});
    }
  }

  ngOnDestroy(): void {
    this.cleanupActivePrintFrame();
  }

  get tableRows(): DocumentsTableRow[] {
    return (this.getAppointmentDocuments()).map((document: AppointmentDocument) => ({
      id: document.id,
      document: document.documentType ?? 'No document',
      url: document.url ?? null,
      date: this.appointmentDate ?? 'No date',
      provider: document.provider?.trim() || this.defaultProvider,
    }));
  }

  printDocument(row: DocumentsTableRow): void {
    if (!this.patientId) {
      this.toastService.showErrorToast('Unable to print the document because the patient could not be identified.');
      return;
    }

    if (this.printingDocumentId !== null) {
      this.toastService.showInfoToast('A document is already being prepared for printing.');
      return;
    }

    this.printingDocumentId = row.id;
    this.documentService.getDocumentBlob$(this.patientId, row.id)
      .pipe(
        take(1),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: (documentBlob) => this.openPrintDialog(documentBlob, row.document),
        error: (error) => this.handlePrintError('The document could not be loaded for printing.', error)
      });
  }

  private openPrintDialog(documentBlob: Blob, documentName: string): void {
    this.cleanupActivePrintFrame();

    const printFileName = this.getPrintFileName(documentName);
    const printFile = new File([documentBlob], printFileName, {
      type: documentBlob.type || 'application/pdf'
    });
    const printUrl = URL.createObjectURL(printFile);
    const printFrame = this.document.createElement('iframe');
    printFrame.title = `Print ${printFileName}`;
    printFrame.style.position = 'fixed';
    printFrame.style.left = '-10000px';
    printFrame.style.bottom = '0';
    printFrame.style.width = '1px';
    printFrame.style.height = '1px';
    printFrame.style.border = '0';
    printFrame.style.opacity = '0';
    printFrame.style.pointerEvents = 'none';

    printFrame.addEventListener('load', () => {
      const printWindow = printFrame.contentWindow;
      if (!printWindow) {
        this.handlePrintError('The browser could not open the document print dialog.');
        return;
      }

      printWindow.addEventListener(
        'afterprint',
        () => this.removePrintFrame(printFrame, printUrl),
        { once: true }
      );

      this.originalDocumentTitle = this.document.title;
      try {
        this.document.title = printFileName.replace(/\.pdf$/i, '');
        printWindow.focus();
        printWindow.print();
        this.printingDocumentId = null;
      } catch (error) {
        this.handlePrintError('The browser could not open the document print dialog.', error);
      }
    }, { once: true });

    printFrame.addEventListener(
      'error',
      () => this.handlePrintError('The document could not be loaded for printing.'),
      { once: true }
    );

    printFrame.src = printUrl;
    this.printFrame = printFrame;
    this.printUrl = printUrl;
    this.document.body.appendChild(printFrame);
    this.printFrameCleanupTimeoutId = setTimeout(
      () => this.removePrintFrame(printFrame, printUrl),
      DocumentsTableComponent.PRINT_FRAME_CLEANUP_DELAY_MS
    );
  }

  private getPrintFileName(documentType: string): string {
    const firstInitial = this.patientFirstName.trim().charAt(0);
    const patientName = `${firstInitial}${this.patientLastName.trim()}`;
    const baseName = [patientName, documentType.trim()]
      .filter(part => part.length > 0)
      .join(' ')
      .replace(/[<>:"/\\|?*]/g, ' ')
      .replace(/\s+/g, ' ')
      .replace(/[. ]+$/g, '')
      .trim();

    return `${baseName || 'Document'}.pdf`;
  }

  private handlePrintError(message: string, error?: unknown): void {
    if (error) {
      console.error(message, error);
    }

    this.printingDocumentId = null;
    this.cleanupActivePrintFrame();
    this.toastService.showErrorToast(message);
  }

  private cleanupActivePrintFrame(): void {
    if (this.printFrame && this.printUrl) {
      this.removePrintFrame(this.printFrame, this.printUrl);
    }
  }

  private removePrintFrame(printFrame: HTMLIFrameElement, printUrl: string): void {
    if (this.printFrame !== printFrame || this.printUrl !== printUrl) {
      return;
    }

    printFrame.remove();
    URL.revokeObjectURL(printUrl);

    if (this.printFrameCleanupTimeoutId !== null) {
      clearTimeout(this.printFrameCleanupTimeoutId);
      this.printFrameCleanupTimeoutId = null;
    }

    this.printFrame = null;
    this.printUrl = null;
    this.restoreDocumentTitle();
  }

  private restoreDocumentTitle(): void {
    if (this.originalDocumentTitle === null) {
      return;
    }

    this.document.title = this.originalDocumentTitle;
    this.originalDocumentTitle = null;
  }

  private getAppointmentDocuments(): AppointmentDocument[] {
    return this.appointmentDocumentData.value() ?? [];  }

}
